/**
 * Adapter B4 (bản đồ chiến lược thành phần / công ty) và B5 (KPI xương cá).
 * Cả ba API lưu từng phần (chỉ xử lý mục gửi lên) nhưng phân loại thêm/sửa/xoá khác nhau:
 *  - B4 thành phần `updatethemestrategymap`: guid rỗng|"temp…" = thêm, "update" = sửa (ghi đè cả dòng), "delete" = xoá.
 *  - B4 công ty `updatestrategymap`: id "temp…" + guid "" = thêm, id thật + guid "" = sửa, guid "delete" = xoá.
 *  - B5 `savefishbone`: KPI id "temp…" = thêm; xoá qua deleteKPIIds.
 * Liên kết nhân quả lưu ở nút nguồn: linkIDs = id các mục tiêu đích (phân tách dấu phẩy).
 */
import { knowledgeApi } from '@/services/knowledge/knowledge-api';
import { perspectiveApi } from '@/services/performance/perspective-api';
import { StrategyMapCompanyAPI } from '@/services/performance/strategy-map-company-api';
import { fishboneApi } from '@/services/performance/fishbone-api';
import { useAuthStore } from '@/store/useAuthStore';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { AiProposal, AiProposalItem } from '@/types/ai-agent';
import type { KienThucItem, KienThucLoai } from '@/types/knowledge';
import type { PerspectiveDTO } from '@/types/step';
import type { ObjectiveMap, PerspectiveMap } from '@/types/performance/buoc4congty';
import CommonJs from '@/utils/commonJs';
import { CONSTANTS } from '@/utils/constants';
import { b1Context } from './knowledgeSteps';
import { AdapterSaveError, type ApplyResult, type DiffRow, type StepAdapter } from './types';

const tenantId = () => useAuthStore.getState().tenantID || '';
const header = () => {
  const h = usePrivateStore.getState().getRequestHeader();
  if (CommonJs.checkNullIDSelectRequestHeader(h)) throw new AdapterSaveError('Chưa chọn bộ phận / giai đoạn.');
  return h;
};
const yearOf = (h: ReturnType<typeof header>) => Number(h.selectPeriod?.year) || new Date().getFullYear();

/** Id tạm độ dài cố định: BE thay id tạm trong linkIDs bằng LIKE, nên không id nào được là tiền tố của id khác. */
export const fixedTempId = () => `temp_ai_${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;

const stripHtml = (html?: string | null) =>
  String(html ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
const toHtml = (text: string) => `<p>${text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
const splitIds = (s?: string | null) => String(s ?? '').split(',').map((x) => x.trim()).filter(Boolean);
const okOrThrow = (res: unknown, fallback: string) => {
  const r = res as { success?: boolean; message?: string } | null;
  if (!r || r.success === false) throw new AdapterSaveError(r?.message || fallback);
  return r as { success?: boolean; data?: unknown };
};

/** Gắn liên kết nguyên nhân → mục tiêu: thêm `targetId` vào linkIDs của từng nút nguồn. */
const addLink = (linkIDs: string | undefined, targetId: string) =>
  Array.from(new Set([...splitIds(linkIDs), targetId])).join(',');

const diffRow = (p: AiProposalItem, sectionLabel: string, before: string, names: Record<string, string>): DiffRow => {
  const causes = ((p.links?.cause_refs as string[]) ?? []).map((c) => names[c] ?? c).filter(Boolean);
  return {
    ref: p.target.ref,
    op: p.op,
    sectionLabel,
    before,
    after: `${String(p.fields.text ?? '')}${causes.length ? `\n← nguyên nhân: ${causes.join('; ')}` : ''}`,
    rationale: p.rationale,
    editable: p.op !== 'delete',
  };
};

// ── B4 thành phần ──────────────────────────────────────────────────────────

const B4_VITRIS = [2, 6, 11, 12, 13, 14, 15, 16, 17];
const STRATEGY_VITRIS = [14, 15, 16, 17];

export interface ThemeMapData {
  perspectives: PerspectiveDTO[];
  objectives: PerspectiveDTO[];
  strategies: KienThucItem[];
}

const loadThemeMap = async (): Promise<ThemeMapData> => {
  const res = okOrThrow(
    await knowledgeApi.listKnowledge({ site: tenantId(), tenBuoc: 'buoc4thanhphan', viTris: B4_VITRIS, requestHeader: header() } as never),
    'Không tải được bản đồ chiến lược.',
  );
  const d = (res.data ?? {}) as {
    listPhuongDien?: PerspectiveDTO[];
    listMucTieu?: PerspectiveDTO[];
    listKienThucLoais?: KienThucLoai[];
    listKienThucs?: KienThucItem[];
  };
  const strategyLoai = new Set((d.listKienThucLoais ?? []).filter((l) => STRATEGY_VITRIS.includes(l.viTri)).map((l) => l.id));
  return {
    perspectives: (d.listPhuongDien ?? []).slice(0, 4),
    objectives: d.listMucTieu ?? [],
    strategies: (d.listKienThucs ?? []).filter((k) => k.chon && strategyLoai.has(k.loaiID ?? '')),
  };
};

const saveThemeMap = async (nodes: PerspectiveDTO[]) => {
  if (!nodes.length) return [] as PerspectiveDTO[];
  const res = okOrThrow(
    await perspectiveApi.bulkSave({ listThemeStrategyMap: nodes, requestHeader: header() } as never),
    'Lưu bản đồ chiến lược thất bại.',
  );
  return (Array.isArray(res.data) ? res.data : []) as PerspectiveDTO[];
};

const b4ProposeContext = async () => {
  const [b1, theme] = await Promise.all([b1Context(), loadThemeMap()]);
  const advantage = (b1.b1?.competitive_advantage ?? []) as { text?: string; id?: string }[];
  return {
    user_context: b1.user_context,
    b1: b1.b1,
    competitive_advantage: advantage.map((x) => ({ id: x.id, text: (x.text ?? '').trim() })).filter((x) => x.text),
    chosen_strategies: theme.strategies.map((s) => ({
      id: s.id,
      text: (s.noiDung ?? '').trim(),
    })),
    perspectives: theme.perspectives.map((p) => ({ id: p.id, text: p.ten })),
  };
};

export const b4ThemeMapAdapter: StepAdapter<ThemeMapData> = {
  key: 'b4.theme_map',
  title: 'Bản đồ chiến lược thành phần',
  load: loadThemeMap,
  buildContext: b4ProposeContext,
  buildSnapshot: (d) => ({
    step: 'b4.theme_map',
    perspectives: d.perspectives.map((p) => ({ id: p.id, text: p.ten })),
    strategies: d.strategies.map((s) => ({ id: s.id, text: (s.noiDung ?? '').trim() })),
    objectives: d.objectives.map((o) => ({
      id: o.id,
      perspective_id: o.parentID,
      strategy_id: (o as PerspectiveDTO & { ma?: string }).ma,
      text: stripHtml(o.moTa),
      leads_to: splitIds(o.linkIDs),
    })),
  }),
  diff: (proposal, d) => {
    const names: Record<string, string> = {};
    d.objectives.forEach((o) => (names[o.id] = stripHtml(o.moTa)));
    proposal.items.forEach((p) => (names[p.target.ref] = String(p.fields.text ?? '')));
    const persp = Object.fromEntries(d.perspectives.map((p) => [p.id, p.ten ?? '']));
    const strat = Object.fromEntries(d.strategies.map((s) => [s.id, (s.noiDung ?? '').trim()]));
    return proposal.items.map((p) => {
      const existing = d.objectives.find((o) => o.id === p.target.id);
      const s = strat[String(p.links?.strategy_id ?? (existing as { ma?: string } | undefined)?.ma ?? '')];
      return diffRow(p, `${persp[p.target.section] ?? 'Phương diện'}${s ? ` · ${s.slice(0, 40)}` : ''}`, existing ? stripHtml(existing.moTa) : '', names);
    });
  },
  apply: async (proposal: AiProposal, selectedRefs, edits): Promise<ApplyResult> => {
    const before = await loadThemeMap();
    const h = header();
    const chosen = proposal.items.filter((p) => selectedRefs.includes(p.target.ref) && p.op !== 'delete');
    const perspById = new Map(before.perspectives.map((p) => [p.id, p]));
    const objById = new Map(before.objectives.map((o) => [o.id, o]));
    const newNodes = new Map<string, PerspectiveDTO>(); // ref → node
    const updates = new Map<string, PerspectiveDTO>(); // id → full row
    const nextViTri: Record<string, number> = {};
    before.objectives.forEach((o) => {
      const k = `${(o as { ma?: string }).ma}|${o.parentID}`;
      nextViTri[k] = Math.max(nextViTri[k] ?? 0, o.viTri ?? 0);
    });
    chosen.forEach((p, i) => {
      const text = edits[p.target.ref] ?? String(p.fields.text ?? '');
      if (p.op === 'update') {
        const row = objById.get(p.target.id);
        if (row) updates.set(row.id, { ...(updates.get(row.id) ?? row), moTa: toHtml(text), guid: 'update' });
        return;
      }
      const group = perspById.get(p.target.section);
      const strategyId = String(p.links?.strategy_id ?? '');
      if (!group || !strategyId) return;
      const k = `${strategyId}|${group.id}`;
      nextViTri[k] = (nextViTri[k] ?? 0) + 1;
      const id = fixedTempId();
      newNodes.set(p.target.ref, {
        id, guid: id, site: tenantId(), ten: 'Mục tiêu thành phần', moTa: toHtml(text),
        phuongDien: false, inHouse: true, viTri: nextViTri[k], nhomCongViecID: 0,
        parentID: group.id, ma: strategyId, idBoPhan: h.selectTree?.idSelected ?? '', idGiaiDoan: h.selectPeriod?.idGiaiDoan ?? '',
        boPhanID: '0', pos: `${30 + i * 24},${60 + i * 24}`, posTop: 60 + i * 24, posLeft: 30 + i * 24, linkIDs: '',
        mauNen: group.mauNen, mauChu: '#000000', mauSpan: group.mauSpan, tiTrong: '', thoiGian: '', bieuMau: '', link: '', param: '',
      } as PerspectiveDTO);
    });
    // cause → effect: linkIDs trên nút nguồn
    chosen.forEach((p) => {
      const targetId = newNodes.get(p.target.ref)?.id ?? (p.op === 'update' ? p.target.id : '');
      if (!targetId) return;
      ((p.links?.cause_refs as string[]) ?? []).forEach((cause) => {
        const srcNew = newNodes.get(cause);
        if (srcNew) srcNew.linkIDs = addLink(srcNew.linkIDs, targetId);
        else if (objById.has(cause)) {
          const row = updates.get(cause) ?? { ...objById.get(cause)!, guid: 'update' };
          updates.set(cause, { ...row, linkIDs: addLink(row.linkIDs, targetId) });
        }
      });
    });
    const created = await saveThemeMap([...newNodes.values(), ...updates.values()]);
    const createdIds = created.slice(0, newNodes.size).map((c) => c.id).filter(Boolean);
    return {
      savedIds: [...createdIds, ...updates.keys()],
      saveEndpoint: 'POST /v1/perspective/updatethemestrategymap',
      undo: async () => {
        const now = await loadThemeMap();
        const nowById = new Map(now.objectives.map((o) => [o.id, o]));
        const restore = [...updates.keys()].map((id) => ({ ...objById.get(id)!, guid: 'update' }));
        const remove = createdIds.filter((id) => nowById.has(id)).map((id) => ({ ...nowById.get(id)!, guid: 'delete' }));
        await saveThemeMap([...restore, ...remove]);
      },
    };
  },
};

// ── B4 công ty ─────────────────────────────────────────────────────────────

export interface CompanyMapData {
  perspectives: PerspectiveMap[];
  objectives: ObjectiveMap[];
}

const loadCompanyMap = async (): Promise<CompanyMapData> => {
  const res = okOrThrow(await StrategyMapCompanyAPI.getStrategyMap(header()), 'Không tải được bản đồ chiến lược công ty.');
  const perspectives = ((res.data ?? []) as PerspectiveMap[]).slice();
  const objectives = perspectives.flatMap((p) =>
    ((p as PerspectiveMap & { listtablebandocongty?: ObjectiveMap[] }).listtablebandocongty ?? []).map((o) => ({
      ...o,
      idPhuongDien: o.idPhuongDien || p.id,
    })),
  );
  return { perspectives, objectives };
};

const saveCompanyMap = async (items: ObjectiveMap[]) => {
  if (!items.length) return;
  const h = header();
  okOrThrow(await StrategyMapCompanyAPI.updateStrategyMap({ items, year: yearOf(h), requestHeader: h }), 'Lưu bản đồ công ty thất bại.');
};

const isPlaceholder = (o: ObjectiveMap) => !o.id || o.id.startsWith('temp');

export const b4CompanyMapAdapter: StepAdapter<CompanyMapData> = {
  key: 'b4.company_map',
  title: 'Bản đồ chiến lược công ty',
  load: loadCompanyMap,
  buildContext: b4ProposeContext,
  buildSnapshot: (d) => ({
    step: 'b4.company_map',
    perspectives: d.perspectives.map((p) => ({ id: p.id, text: p.ten })),
    objectives: d.objectives.filter((o) => !isPlaceholder(o)).map((o) => ({
      id: o.id, perspective_id: o.idPhuongDien, text: o.ten, leads_to: splitIds(o.linkIDs),
    })),
    component_objectives: d.objectives.filter(isPlaceholder).map((o) => ({ perspective_id: o.idPhuongDien, text: o.ten })),
  }),
  diff: (proposal, d) => {
    const names: Record<string, string> = {};
    d.objectives.forEach((o) => (names[o.id] = o.ten));
    proposal.items.forEach((p) => (names[p.target.ref] = String(p.fields.text ?? '')));
    const persp = Object.fromEntries(d.perspectives.map((p) => [p.id, p.ten ?? '']));
    return proposal.items.map((p) =>
      diffRow(p, persp[p.target.section] ?? 'Phương diện', d.objectives.find((o) => o.id === p.target.id)?.ten ?? '', names),
    );
  },
  apply: async (proposal, selectedRefs, edits) => {
    const before = await loadCompanyMap();
    const h = header();
    const year = yearOf(h);
    const real = before.objectives.filter((o) => !isPlaceholder(o));
    const objById = new Map(real.map((o) => [o.id, o]));
    const chosen = proposal.items.filter((p) => selectedRefs.includes(p.target.ref) && p.op !== 'delete');
    const newItems = new Map<string, ObjectiveMap>();
    const updates = new Map<string, ObjectiveMap>();
    const nextViTri: Record<string, number> = {};
    real.forEach((o) => (nextViTri[o.idPhuongDien] = Math.max(nextViTri[o.idPhuongDien] ?? 0, o.viTri ?? 0)));
    chosen.forEach((p, i) => {
      const text = edits[p.target.ref] ?? String(p.fields.text ?? '');
      if (p.op === 'update') {
        const row = objById.get(p.target.id);
        if (row) updates.set(row.id, { ...(updates.get(row.id) ?? row), ten: text, guid: '' });
        return;
      }
      if (!before.perspectives.some((x) => x.id === p.target.section)) return;
      nextViTri[p.target.section] = (nextViTri[p.target.section] ?? 0) + 1;
      newItems.set(p.target.ref, {
        id: fixedTempId(), guid: '', idParent: '0', idPhuongDien: p.target.section, idThanhPhan: '', ten: text,
        param: '', linkIDs: '', pos: `${30 + i * 24},${60 + i * 24}`, posTop: 60 + i * 24, posLeft: 30 + i * 24,
        trenDuoi: 0, viTri: nextViTri[p.target.section], year, listKPIMap: [], listChild: [],
      });
    });
    chosen.forEach((p) => {
      const targetId = newItems.get(p.target.ref)?.id ?? (p.op === 'update' ? p.target.id : '');
      if (!targetId) return;
      ((p.links?.cause_refs as string[]) ?? []).forEach((cause) => {
        const srcNew = newItems.get(cause);
        if (srcNew) srcNew.linkIDs = addLink(srcNew.linkIDs, targetId);
        else if (objById.has(cause)) {
          const row = updates.get(cause) ?? { ...objById.get(cause)!, guid: '' };
          updates.set(cause, { ...row, linkIDs: addLink(row.linkIDs, targetId) });
        }
      });
    });
    await saveCompanyMap([...newItems.values(), ...updates.values()]);
    const after = await loadCompanyMap();
    const beforeIds = new Set(real.map((o) => o.id));
    const createdIds = after.objectives.filter((o) => !isPlaceholder(o) && !beforeIds.has(o.id)).map((o) => o.id);
    return {
      savedIds: [...createdIds, ...updates.keys()],
      saveEndpoint: 'POST /v1/performance/updatestrategymap',
      undo: async () => {
        const restore = [...updates.keys()].map((id) => ({ ...objById.get(id)!, guid: '' }));
        const remove = createdIds.map((id) => ({ id, guid: 'delete' }) as ObjectiveMap);
        await saveCompanyMap([...restore, ...remove]);
      },
    };
  },
};

// ── B5 KPI xương cá ────────────────────────────────────────────────────────

interface FishboneKpi {
  id: string;
  ten: string;
  viTri: number;
}
interface FishboneNode {
  id: string;
  ten: string;
  idParent?: string;
  idPhuongDien?: string;
  viTri: number;
  trenDuoi?: number;
  type?: string;
  listKPIMap: FishboneKpi[];
  listChild?: FishboneNode[];
}
export interface FishboneData {
  heads: FishboneNode[];
}

const loadFishbone = async (): Promise<FishboneData> => {
  const res = okOrThrow(
    await fishboneApi.getFishbone({ id: '', site: tenantId(), requestHeader: header() } as never),
    'Không tải được mô hình xương cá.',
  );
  return { heads: (Array.isArray(res.data) ? res.data : []) as FishboneNode[] };
};

const saveFishbone = async (payload: { objectives: unknown[]; deleteKPIIds: string[]; deleteObjectiveIds?: string[] }) => {
  const h = header();
  okOrThrow(
    await fishboneApi.saveFishbone({
      objectives: payload.objectives as ObjectiveMap[],
      deleteObjectiveIds: payload.deleteObjectiveIds ?? [],
      deleteKPIIds: payload.deleteKPIIds,
      requestDTO: { year: yearOf(h), requestHeader: h },
    } as never),
    'Lưu mô hình xương cá thất bại.',
  );
};

/** Mọi mục tiêu (đầu cá + xương) kèm đầu cá chứa nó. */
const allObjectives = (d: FishboneData) =>
  d.heads.flatMap((head) => [
    { node: head, head, isHead: true },
    ...(head.listChild ?? []).map((bone) => ({ node: bone, head, isHead: false })),
  ]);

const b5Context = async () => {
  const [b1, fb] = await Promise.all([b1Context(), loadFishbone()]);
  return {
    user_context: b1.user_context,
    fishbone_heads: fb.heads.map((h) => ({ id: h.id, text: h.ten })),
  };
};

const b5Snapshot = (step: string) => (d: FishboneData) => ({
  step,
  objectives: allObjectives(d).map(({ node, head, isHead }) => ({
    id: node.id,
    text: node.ten,
    type: isHead ? 'dau_ca' : 'xuong_ca',
    head_id: head.id,
    kpis: (node.listKPIMap ?? []).map((k) => k.ten).filter(Boolean),
  })),
});

export const b5FishboneAdapter: StepAdapter<FishboneData> = {
  key: 'b5.fishbone',
  title: 'KPI xương cá',
  load: loadFishbone,
  buildContext: b5Context,
  buildSnapshot: b5Snapshot('b5.fishbone'),
  diff: (proposal, d) => {
    const byId = new Map(allObjectives(d).map((x) => [x.node.id, x.node]));
    return proposal.items.map((p) => {
      const objective = byId.get(String(p.links?.objective_id ?? ''));
      return {
        ref: p.target.ref,
        op: 'add',
        sectionLabel: `KPI · ${(objective?.ten ?? '').slice(0, 60)}`,
        before: '',
        after: `${String(p.fields.name ?? '')}${p.fields.note ? ` — ${String(p.fields.note)}` : ''}`,
        rationale: p.rationale,
        editable: true,
        warning: objective ? undefined : 'Mục tiêu không còn trên mô hình',
      };
    });
  },
  apply: async (proposal, selectedRefs, edits) => {
    const before = await loadFishbone();
    const h = header();
    const index = new Map(allObjectives(before).map((x) => [x.node.id, x]));
    const groups = new Map<string, AiProposalItem[]>();
    proposal.items
      .filter((p) => selectedRefs.includes(p.target.ref))
      .forEach((p) => {
        const id = String(p.links?.objective_id ?? '');
        if (index.has(id)) groups.set(id, [...(groups.get(id) ?? []), p]);
      });
    const objectives = [...groups.entries()].map(([id, items]) => {
      const { node, head, isHead } = index.get(id)!;
      let viTri = Math.max(0, ...(node.listKPIMap ?? []).map((k) => k.viTri ?? 0));
      return {
        ID: node.id,
        IDParent: isHead ? node.idParent || '0' : head.id,
        IDPhuongDien: head.idPhuongDien ?? '',
        Ten: node.ten,
        ViTri: node.viTri,
        TrenDuoi: node.trenDuoi ?? 0,
        Type: isHead ? 'dauca' : 'xuongca',
        LinkIDs: '',
        Param: '',
        ListChild: [],
        ListKPIMap: items.map((p) => {
          const edited = edits[p.target.ref];
          const [name, note] = edited !== undefined ? edited.split(' — ') : [String(p.fields.name ?? ''), String(p.fields.note ?? '')];
          viTri += 1;
          return {
            id: fixedTempId(), ten: (name ?? '').trim(), ghiChu: (note ?? '').trim(), viTri,
            kpiBPPhuTrach: {
              listIDPhuTrach: isHead ? [h.selectTree?.idSelected ?? ''].filter(Boolean) : [],
              listTenVietTat: [], listTenFull: [], listIDPhuTrachRemoveKPI: [],
            },
          };
        }),
      };
    });
    if (objectives.length) await saveFishbone({ objectives, deleteKPIIds: [] });
    const after = await loadFishbone();
    const beforeKpis = new Set(allObjectives(before).flatMap((x) => (x.node.listKPIMap ?? []).map((k) => k.id)));
    const createdIds = allObjectives(after)
      .filter((x) => groups.has(x.node.id))
      .flatMap((x) => (x.node.listKPIMap ?? []).map((k) => k.id))
      .filter((kid) => kid && !beforeKpis.has(kid));
    return {
      savedIds: createdIds,
      saveEndpoint: 'POST /v1/performance/savefishbone',
      undo: async () => {
        if (createdIds.length) await saveFishbone({ objectives: [], deleteKPIIds: createdIds });
      },
    };
  },
};

// ── B5 xương cá (CSF) + KPI cho một đầu cá — v1 buoc5xuongca ─────────────────

type CsfKpi = { name?: string; note?: string };
const csfKpis = (p: AiProposalItem) => (Array.isArray(p.fields.kpis) ? (p.fields.kpis as CsfKpi[]) : []);

export const b5CsfAdapter: StepAdapter<FishboneData> = {
  key: 'b5.csf',
  title: 'Xương cá (CSF) + KPI',
  load: loadFishbone,
  buildContext: b5Context,
  buildSnapshot: b5Snapshot('b5.csf'),
  diff: (proposal, d) => {
    const heads = new Map(d.heads.map((h) => [h.id, h]));
    return proposal.items.map((p) => {
      const head = heads.get(String(p.links?.head_id ?? ''));
      const kpis = csfKpis(p).map((k) => k.name).filter(Boolean);
      return {
        ref: p.target.ref,
        op: 'add',
        sectionLabel: `Xương · ${(head?.ten ?? '').slice(0, 60)}`,
        before: '',
        after: `${String(p.fields.text ?? '')}${kpis.length ? ` — KPI: ${kpis.join('; ')}` : ''}`,
        rationale: p.rationale,
        editable: false,
        warning: head ? undefined : 'Đầu cá không còn trên mô hình',
      };
    });
  },
  apply: async (proposal, selectedRefs) => {
    const before = await loadFishbone();
    const items = proposal.items.filter((p) => selectedRefs.includes(p.target.ref));
    const headId = String(items[0]?.links?.head_id ?? '');
    const head = before.heads.find((x) => x.id === headId);
    if (!head) throw new AdapterSaveError('Đầu cá không còn trên mô hình.');
    const existing = head.listChild ?? [];
    let viTri = Math.max(-1, ...existing.map((b) => b.viTri ?? 0));
    // v1 AIBuoc5: nửa đầu xương phía trên, nửa sau phía dưới (tính cả xương đã có)
    const upperCount = Math.ceil((existing.length + items.length) / 2);
    const objectives = items.map((p, i) => {
      viTri += 1;
      return {
        ID: fixedTempId(),
        IDParent: head.id,
        IDPhuongDien: head.idPhuongDien ?? '',
        IDThanhPhan: '',
        Ten: String(p.fields.text ?? '').trim(),
        ViTri: viTri,
        TrenDuoi: existing.length + i < upperCount ? CONSTANTS.FISHBONE.UPPER_BONE : CONSTANTS.FISHBONE.LOWER_BONE,
        Type: 'xuongca',
        LinkIDs: '',
        Param: '',
        ListChild: [],
        ListKPIMap: csfKpis(p)
          .filter((k) => (k.name ?? '').trim())
          .map((k, j) => ({
            id: fixedTempId(), ten: (k.name ?? '').trim(), ghiChu: (k.note ?? '').trim(), viTri: j,
            kpiBPPhuTrach: { listIDPhuTrach: [], listTenVietTat: [], listTenFull: [], listIDPhuTrachRemoveKPI: [] },
          })),
      };
    });
    if (!objectives.length) return { savedIds: [], saveEndpoint: 'POST /v1/performance/savefishbone', undo: async () => {} };
    await saveFishbone({ objectives, deleteKPIIds: [] });
    const after = await loadFishbone();
    const beforeIds = new Set(existing.map((b) => b.id));
    const createdIds = (after.heads.find((x) => x.id === head.id)?.listChild ?? [])
      .map((b) => b.id)
      .filter((id) => id && !beforeIds.has(id));
    return {
      savedIds: createdIds,
      saveEndpoint: 'POST /v1/performance/savefishbone',
      undo: async () => {
        if (createdIds.length) await saveFishbone({ objectives: [], deleteKPIIds: [], deleteObjectiveIds: createdIds });
      },
    };
  },
};
